// Phase 15 · Portal — My Day Summary. SE's equivalent (MyDaySummary.jsx)
// reads from `/daily-working-summary`, a record only ever created by the
// attendance Day-End close-out flow — a distributor never punches
// attendance or closes a day, so that endpoint would always be empty for
// them. This computes the same shape of numbers live from the
// distributor's own visits + orders instead (no new backend endpoint).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar, Phone, Target, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const DAYS_BACK = 29;

function isoDaysAgo(n) {
  return new Date(Date.now() + 5.5 * 3600 * 1000 - n * 86400000).toISOString().slice(0, 10);
}

// Both /visits and /orders cap page_size at 100 server-side — over a
// 30-day window an active distributor's visit/order count can genuinely
// exceed that in one page, so page through instead of requesting more
// than the endpoint allows (that 422'd with "Input should be less than
// or equal to 100" before this fix).
async function fetchAllPages(path, params, hardCap = 1000) {
  const pageSize = 100;
  let page = 1;
  let all = [];
  for (;;) {
    const { data } = await api.get(path, { params: { ...params, page, page_size: pageSize } });
    const items = data.items || [];
    all = all.concat(items);
    if (items.length < pageSize || all.length >= hardCap || all.length >= (data.total ?? all.length)) break;
    page += 1;
  }
  return all;
}
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function PortalDaySummary() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => isoDaysAgo(0));
  const [loading, setLoading] = useState(true);
  const [byDate, setByDate] = useState(new Map());

  useEffect(() => {
    const date_from = isoDaysAgo(DAYS_BACK);
    const date_to = isoDaysAgo(0);
    setLoading(true);
    Promise.all([
      fetchAllPages("/visits", { user_id: user.id, date_from, date_to }),
      // channel=secondary — same reasoning as Home.jsx: this is a "beat
      // work" summary (calls + orders booked on them), so a distributor's
      // own primary order (distributor → company, a separate channel)
      // must not get summed into these totals.
      fetchAllPages("/orders", { user_id: user.id, date_from, date_to, enrich: "grid", channel: "secondary" }),
    ]).then(async ([v, o]) => {
      const beatIds = [...new Set(v.map((r) => r.beat_id).filter(Boolean))];
      let beatNameById = new Map();
      if (beatIds.length) {
        const { data } = await api.get("/beats", { params: { ids: beatIds.join(","), page_size: beatIds.length } });
        beatNameById = new Map((data.items || []).map((b) => [b.id, b.name]));
      }
      const map = new Map();
      const bucket = (date) => {
        if (!map.has(date)) {
          map.set(date, { date, tc: 0, pc: 0, npc: 0, orders: 0, kg: 0, value: 0,
                            beatNames: new Set(), firstCall: null, lastCall: null });
        }
        return map.get(date);
      };
      for (const r of v) {
        const date = (r.check_in_ts || "").slice(0, 10);
        if (!date) continue;
        const b = bucket(date);
        b.tc += 1;
        if (r.status === "completed" && r.outcome === "productive") b.pc += 1;
        if (r.outcome === "non_productive") b.npc += 1;
        if (r.beat_id && beatNameById.has(r.beat_id)) b.beatNames.add(beatNameById.get(r.beat_id));
        if (!b.firstCall || r.check_in_ts < b.firstCall) b.firstCall = r.check_in_ts;
        if (!b.lastCall || (r.check_out_ts || r.check_in_ts) > b.lastCall) b.lastCall = r.check_out_ts || r.check_in_ts;
      }
      for (const r of o) {
        const date = r.order_date || (r.booking_ts || "").slice(0, 10);
        if (!date) continue;
        const b = bucket(date);
        b.orders += 1;
        b.kg += Number(r.total_qty_kg) || 0;
        b.value += Number(r.net_amount) || 0;
      }
      setByDate(map);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [user.id]);

  const rows = useMemo(
    () => [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date)),
    [byDate]
  );
  const latest = byDate.get(selectedDate) || null;

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="portal-day-summary-page">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 flex items-center gap-1"
              data-testid="portal-day-summary-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">My day summary</div>
        <h1 className="text-2xl font-extrabold text-neutral-900">Daily working summary</h1>
      </div>

      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-neutral-500" />
        <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
               data-testid="portal-day-summary-date-picker" className="h-9 max-w-[220px]" />
      </div>

      {loading && (
        <div className="py-8 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {!loading && !latest && (
        <Card className="border-dashed border-neutral-300 rounded-2xl" data-testid="portal-day-summary-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            No activity on {selectedDate}.
          </CardContent>
        </Card>
      )}

      {latest && (
        <>
          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" /> Calls · {latest.date}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] text-neutral-500">TC</div>
                  <div className="text-2xl font-extrabold" data-testid="portal-dws-tc">{latest.tc}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">PC</div>
                  <div className="text-2xl font-extrabold text-emerald-700" data-testid="portal-dws-pc">{latest.pc}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500" title="Non-Productive Calls">NPC</div>
                  <div className="text-2xl font-extrabold text-neutral-700">{latest.npc}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-neutral-100">
                <div>
                  <div className="text-[11px] text-neutral-500">Start call time</div>
                  <div className="text-sm font-semibold" data-testid="portal-dws-first-call">{fmtTime(latest.firstCall)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-neutral-500">End call time</div>
                  <div className="text-sm font-semibold" data-testid="portal-dws-last-call">{fmtTime(latest.lastCall)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 rounded-2xl">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <Target className="h-3.5 w-3.5" /> Orders
              </div>
              <div className="mt-2 flex items-baseline gap-4">
                <div className="text-2xl font-extrabold" data-testid="portal-dws-orders">{latest.orders}</div>
                <div className="text-xs text-neutral-500">orders</div>
                <div className="text-2xl font-extrabold" data-testid="portal-dws-kg">{latest.kg.toFixed(1)} kg</div>
                <div className="text-xs text-neutral-500 font-mono" data-testid="portal-dws-value">
                  {INR.format(latest.value)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 rounded-2xl" data-testid="portal-dws-beats">
            <CardContent className="p-4">
              <div className="text-[11px] text-neutral-500 uppercase tracking-widest flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" /> Beat(s) worked
              </div>
              <div className="mt-1 text-sm font-medium text-neutral-900">
                {latest.beatNames.size ? [...latest.beatNames].join(", ") : "—"}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {rows.length > 1 && (
        <Card className="border-neutral-200 rounded-2xl" data-testid="portal-dws-history">
          <CardContent className="p-4">
            <div className="text-[11px] text-neutral-500 uppercase tracking-widest">Past days — tap to view</div>
            <div className="mt-2 space-y-1">
              {rows.filter((r) => r.date !== selectedDate).slice(0, 30).map((r) => (
                <button key={r.date} type="button" onClick={() => setSelectedDate(r.date)}
                        data-testid={`portal-dws-history-row-${r.date}`}
                        className="w-full flex items-baseline justify-between text-sm py-2 border-b border-neutral-100 last:border-b-0 text-left hover:bg-neutral-50 rounded-lg px-1 -mx-1">
                  <div className="font-mono text-neutral-500">{r.date}</div>
                  <div className="text-neutral-800">{r.tc} calls · {r.kg.toFixed(1)} kg</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
